import { usePendingConflicts } from '@/api/hooks';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/cn';
import { canManage } from '@/lib/rbac';
import { UserAvatar } from '@/components/ui/UserAvatar';
import { ChevronDown, CreditCard, LogOut, MapPin, Settings, Shuffle } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

const menuItems = [
  { to: '/propriedades', icon: MapPin, label: 'Fazendas' },
  { to: '/configuracoes', icon: Settings, label: 'Minha conta' },
  { to: '/assinatura', icon: CreditCard, label: 'Assinatura' },
];

export function UserMenu() {
  const { user, logout } = useAuth();
  const { data: pending = 0 } = usePendingConflicts();
  const isManager = user ? canManage(user.role) : false;
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;

    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }

    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }

    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  async function handleLogout() {
    setOpen(false);
    await logout();
    navigate('/login');
  }

  if (!user) return null;

  return (
    <div className="relative shrink-0" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="flex items-center gap-2 rounded-xl border border-manejai-sand bg-white px-2.5 py-2 text-sm hover:bg-manejai-cream/50 transition-colors"
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label="Menu do usuário"
      >
        <UserAvatar user={user} size="sm" />
        <div className="hidden sm:block text-left max-w-[160px]">
          <p className="font-medium text-manejai-forest truncate leading-tight">{user.name}</p>
          <p className="text-[11px] text-manejai-bark/60 truncate">{user.email}</p>
        </div>
        <ChevronDown
          className={cn('h-4 w-4 text-manejai-bark/60 transition-transform', open && 'rotate-180')}
        />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 mt-2 w-56 rounded-xl border border-manejai-sand bg-white shadow-elevated py-1 z-50"
        >
          <div className="px-4 py-3 border-b border-manejai-sand/60 sm:hidden">
            <p className="text-sm font-medium text-manejai-forest truncate">{user.name}</p>
            <p className="text-xs text-manejai-bark/60 truncate">{user.email}</p>
          </div>

          {menuItems.map(({ to, icon: Icon, label }) => (
            <Link
              key={to}
              to={to}
              role="menuitem"
              onClick={() => setOpen(false)}
              className="flex items-center gap-3 px-4 py-2.5 text-sm text-manejai-bark hover:bg-manejai-cream/50 transition-colors"
            >
              <Icon className="h-4 w-4 text-manejai-moss shrink-0" />
              {label}
            </Link>
          ))}

          {isManager && (
            <Link
              to="/conflitos"
              role="menuitem"
              onClick={() => setOpen(false)}
              className="flex items-center gap-3 px-4 py-2.5 text-sm text-manejai-bark hover:bg-manejai-cream/50 transition-colors"
            >
              <Shuffle className="h-4 w-4 text-manejai-moss shrink-0" />
              <span className="flex-1">Conflitos sync</span>
              {pending > 0 && (
                <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-manejai-clay px-1.5 text-[10px] font-bold text-white">
                  {pending}
                </span>
              )}
            </Link>
          )}

          <div className="border-t border-manejai-sand/60 mt-1 pt-1">
            <button
              type="button"
              role="menuitem"
              onClick={handleLogout}
              className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-manejai-bark hover:bg-manejai-cream/50 transition-colors"
            >
              <LogOut className="h-4 w-4 text-manejai-moss shrink-0" />
              Sair
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
